import React, { useEffect, useId, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronDown, MoreHorizontal, Search, X } from 'lucide-react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import './adminKit.css';

/**
 * Admin kit — building blocks for the Admin pages (see adminKit.css).
 * Everything here is presentational; data and saving stay in the pages.
 */

export function AdminPage({ children, className }) {
  return <div className={`ak-page${className ? ` ${className}` : ''}`}>{children}</div>;
}

/**
 * Head band: optional back crumb, optional org mark, title + subtitle,
 * ink-pill tabs, right-aligned actions.
 *   tabs [{ key, label, count?, dot? }]
 */
export function AdminHead({ title, subtitle, mark, crumb, tabs, activeTab, onTabChange, actions }) {
  return (
    <header className="ak-head">
      <div className="ak-head-id">
        {mark ? <div className="ak-orgmark" aria-hidden="true">{mark}</div> : null}
        <div className="ak-head-text">
          {crumb ? (
            <button type="button" className="ak-crumb" onClick={crumb.onClick}>
              ← {crumb.label}
            </button>
          ) : null}
          <h1>{title}</h1>
          {subtitle ? <div className="ak-sub">{subtitle}</div> : null}
        </div>
      </div>
      {Array.isArray(tabs) && tabs.length > 1 ? (
        <Tabs tabs={tabs} value={activeTab} onChange={onTabChange} label={`${typeof title === 'string' ? title : 'Page'} sections`} />
      ) : null}
      {actions ? <div className="ak-head-actions">{actions}</div> : null}
    </header>
  );
}

export function Tabs({ tabs, value, onChange, label }) {
  return (
    <div className="ak-tabs" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={value === t.key}
          onClick={() => onChange && onChange(t.key)}
        >
          {t.label}
          {t.count != null ? <span className="ak-tab-count ak-mono">{t.count}</span> : null}
          {t.dot ? <span className="ak-tab-dot" aria-label="Needs attention" /> : null}
        </button>
      ))}
    </div>
  );
}

export function Button({ variant, size, icon: Icon, children, className, ...rest }) {
  const cls = ['ak-btn'];
  if (variant === 'primary') cls.push('ak-primary');
  if (variant === 'ghost') cls.push('ak-ghost');
  if (variant === 'danger') cls.push('ak-danger');
  if (variant === 'danger-solid') cls.push('ak-danger', 'ak-solid');
  if (size === 'small') cls.push('ak-small');
  if (!children && Icon) cls.push('ak-icon');
  if (className) cls.push(className);
  return (
    <button type="button" className={cls.join(' ')} {...rest}>
      {Icon ? <Icon aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

/** stats [{ key, label, value, sub?, tone?: 'bad', onClick? }] */
export function StatTiles({ stats }) {
  return (
    <div className="ak-stats">
      {stats.map((s) => {
        const Tag = s.onClick ? 'button' : 'div';
        return (
          <Tag
            key={s.key || s.label}
            type={s.onClick ? 'button' : undefined}
            className={`ak-stat${s.tone === 'bad' ? ' ak-bad' : ''}`}
            onClick={s.onClick}
          >
            <span className="ak-stat-label">{s.label}</span>
            <span className="ak-stat-value">{s.value}</span>
            {s.sub ? <span className="ak-stat-sub">{s.sub}</span> : null}
          </Tag>
        );
      })}
    </div>
  );
}

export function SearchField({ value, onChange, placeholder, label, autoFocus }) {
  return (
    <label className="ak-search">
      <Search aria-hidden="true" />
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={label || placeholder}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/**
 * Segmented control.
 *   options [{ value, label, count? }]
 *   mode 'filter' (ink pressed state) | 'choice' (soft, radio semantics)
 */
export function Segmented({ options, value, onChange, label, mode = 'filter', fill = false, disabled = false }) {
  const radio = mode === 'choice';
  return (
    <div
      className={`ak-seg${radio ? ' ak-soft' : ''}${fill ? ' ak-fill' : ''}`}
      role={radio ? 'radiogroup' : 'group'}
      aria-label={label}
    >
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={String(o.value)}
            type="button"
            disabled={disabled || o.disabled}
            role={radio ? 'radio' : undefined}
            aria-checked={radio ? on : undefined}
            aria-pressed={radio ? undefined : on}
            onClick={() => onChange(o.value)}
          >
            {o.label}
            {o.count != null ? <span className="ak-seg-count ak-mono">{o.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export function Switch({ checked, onChange, label, disabled, title }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={Boolean(checked)}
      aria-label={typeof label === 'string' ? undefined : title}
      className="ak-switch"
      disabled={disabled}
      title={title}
      onClick={() => onChange(!checked)}
    >
      <span className="ak-switch-track" aria-hidden="true" />
      {label ? <span>{label}</span> : null}
    </button>
  );
}

/** tone: ok | bad | warn | info | off (default) */
export function StatusPill({ tone, children, running, dot = true, title }) {
  const cls = ['ak-pill'];
  if (tone && tone !== 'off') cls.push(`ak-${tone}`);
  if (running) cls.push('ak-run');
  if (!dot) cls.push('ak-nodot');
  return <span className={cls.join(' ')} title={title}>{children}</span>;
}

export function Badge({ tone, children, title }) {
  return <span className={`ak-badge${tone ? ` ak-${tone}` : ''}`} title={title}>{children}</span>;
}

export function Tag({ children, onRemove, removeLabel }) {
  return (
    <span className={`ak-tag${onRemove ? '' : ' ak-static'}`}>
      {children}
      {onRemove ? (
        <button type="button" aria-label={removeLabel || `Remove ${typeof children === 'string' ? children : ''}`} onClick={onRemove}>×</button>
      ) : null}
    </span>
  );
}

/** "Ana Lee" → "AL"; falls back to the email, then "?". */
export function initialsOf(name, email) {
  const src = (name || email || '?').trim();
  const parts = src.split(/[\s.@_-]+/).filter(Boolean);
  return ((parts[0] || '?')[0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
}

/** `as="span"` when the avatar sits inside a button (phone card rows). */
export function Avatar({ name, email, large, as: Tag = 'div' }) {
  return <Tag className={`ak-avatar${large ? ' ak-lg' : ''}`} aria-hidden="true">{initialsOf(name, email)}</Tag>;
}

export function Card({ title, actions, children, className }) {
  return (
    <section className={`ak-card${className ? ` ${className}` : ''}`}>
      {title || actions ? (
        <div className="ak-card-head">
          {title ? <h3>{title}</h3> : <span style={{ flex: 1 }} />}
          {actions}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** Label + control + help/error. Pass the control as children. */
export function Field({ label, help, error, required, children, className, htmlFor }) {
  return (
    <div className={`ak-field${className ? ` ${className}` : ''}`}>
      {label ? (
        <label className="ak-label" htmlFor={htmlFor}>
          {label}
          {required ? <span className="ak-req" aria-hidden="true">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? <span className="ak-field-error" role="alert">{error}</span> : help ? <span className="ak-help">{help}</span> : null}
    </div>
  );
}

export function Banner({ tone, icon: Icon, title, children, action }) {
  return (
    <div className={`ak-banner${tone ? ` ak-${tone}` : ''}`} role={tone === 'bad' ? 'alert' : 'status'}>
      {Icon ? <Icon aria-hidden="true" /> : null}
      <div>
        {title ? <strong>{title}</strong> : null}
        {children ? <span>{children}</span> : null}
      </div>
      {action}
    </div>
  );
}

export function EmptyRow({ colSpan, title, body, action }) {
  return (
    <tr>
      <td colSpan={colSpan}>
        <div className="ak-empty">
          {title ? <strong>{title}</strong> : null}
          {body}
          {action ? <div style={{ marginTop: 12 }}>{action}</div> : null}
        </div>
      </td>
    </tr>
  );
}

export function SkeletonRows({ rows = 6, columns = 6 }) {
  return Array.from({ length: rows }).map((_, r) => (
    <tr key={r} aria-hidden="true">
      {Array.from({ length: columns }).map((__, c) => (
        <td key={c}><div className="ak-sk" style={{ width: `${40 + ((r * 7 + c * 13) % 50)}%` }} /></td>
      ))}
    </tr>
  ));
}

// ── Phone card lists (≤768px) ─────────────────────────────────────────────
// Pages switch to these with useIsMobile(); desktop keeps its tables.

export function MobileList({ children, label, busy }) {
  return (
    <ul className={`ak-m-list${busy ? ' ak-m-busy' : ''}`} aria-label={label} aria-busy={busy ? 'true' : 'false'}>
      {children}
    </ul>
  );
}

/**
 * One tappable card row. `lead` (avatar / mark), `title`, `sub` (second
 * line), `aside` (right column), `trailing` (a sibling control outside the
 * main button, e.g. a 44px "…" button), `children` (expanded detail below).
 * Without onClick the row is static.
 */
export function MobileRow({
  lead, title, sub, aside, trailing, children, onClick, faded, selected, expanded, label, className,
}) {
  const cls = ['ak-m-row'];
  if (faded) cls.push('ak-m-faded');
  if (selected) cls.push('ak-m-selected');
  if (className) cls.push(className);
  const body = (
    <>
      {lead}
      <span className="ak-m-row-text">
        <span className="ak-m-row-title">{title}</span>
        {sub ? <span className="ak-m-row-sub">{sub}</span> : null}
      </span>
      {aside ? <span className="ak-m-row-aside">{aside}</span> : null}
    </>
  );
  return (
    <li className={cls.join(' ')}>
      <div className="ak-m-row-line">
        {onClick ? (
          <button type="button" className="ak-m-row-main" onClick={onClick} aria-label={label} aria-expanded={expanded}>
            {body}
          </button>
        ) : (
          <div className="ak-m-row-main">{body}</div>
        )}
        {trailing}
      </div>
      {children}
    </li>
  );
}

export function MobileListEmpty({ title, body, action }) {
  return (
    <li className="ak-m-note">
      <div className="ak-empty">
        {title ? <strong>{title}</strong> : null}
        {body}
        {action ? <div style={{ marginTop: 12 }}>{action}</div> : null}
      </div>
    </li>
  );
}

export function MobileListError({ children, onRetry }) {
  return (
    <li className="ak-m-note">
      <div className="ak-error" role="alert">
        <span style={{ flex: 1 }}>{children}</span>
        {onRetry ? <Button size="small" onClick={onRetry}>Retry</Button> : null}
      </div>
    </li>
  );
}

export function MobileListSkeleton({ rows = 6, lead = true }) {
  return Array.from({ length: rows }).map((_, r) => (
    <li key={r} className="ak-m-row" aria-hidden="true">
      <div className="ak-m-row-line">
        <div className="ak-m-row-main">
          {lead ? <span className="ak-m-sk-lead" /> : null}
          <span className="ak-m-row-text">
            <span className="ak-sk" style={{ width: `${45 + ((r * 17) % 35)}%` }} />
            <span className="ak-sk" style={{ width: `${30 + ((r * 11) % 30)}%` }} />
          </span>
        </div>
      </div>
    </li>
  ));
}

/** Label / value rows for phone cards (schedule, run details). */
export function KeyValues({ rows }) {
  return (
    <dl className="ak-m-kv">
      {rows.filter(Boolean).map((r) => (
        <div key={r.key || r.label} className="ak-m-kv-row">
          <dt>{r.label}</dt>
          <dd>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function BulkBar({ count, noun = 'selected', onClear, children }) {
  if (!count) return null;
  return (
    <div className="ak-bulk" role="region" aria-label="Bulk actions">
      <strong>{count} {noun}</strong>
      {children}
      <button type="button" className="ak-btn ak-clear" onClick={onClear}>Clear</button>
    </div>
  );
}

/**
 * Chip that opens a checklist. Empty selection = no filter.
 *   options [{ value, label, hint? }], value [] , onChange(next[])
 */
export function FilterMenu({ label, options, value = [], onChange, searchable = false, single = false }) {
  const [q, setQ] = useState('');
  const selected = new Set(value.map(String));
  const shown = q
    ? options.filter((o) => String(o.label).toLowerCase().includes(q.toLowerCase()))
    : options;
  const active = value.length > 0;
  const summary = active
    ? (value.length === 1 ? (options.find((o) => String(o.value) === String(value[0]))?.label ?? value[0]) : `${value.length} selected`)
    : null;
  const toggle = (v) => {
    const key = String(v);
    if (single) { onChange(selected.has(key) ? [] : [v]); return; }
    const next = selected.has(key) ? value.filter((x) => String(x) !== key) : [...value, v];
    onChange(next);
  };
  return (
    <DropdownMenu onOpenChange={(open) => { if (!open) setQ(''); }}>
      <DropdownMenuTrigger asChild>
        <button type="button" className={`ak-chip${active ? ' ak-active' : ''}`}>
          {label}{summary ? `: ${summary}` : ''}
          <ChevronDown aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[220px]">
        {searchable ? (
          <div className="ak-menu-search">
            <input
              value={q}
              placeholder={`Search ${String(label).toLowerCase()}`}
              aria-label={`Search ${label}`}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        ) : null}
        <div className="ak-menu-list">
          {shown.map((o) => (
            <DropdownMenuPrimitive.CheckboxItem
              key={String(o.value)}
              checked={selected.has(String(o.value))}
              onSelect={(e) => { e.preventDefault(); toggle(o.value); }}
              className="relative flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none focus:bg-[var(--lodge-onyx-50)]"
            >
              <span className="ak-menu-item-check">
                <DropdownMenuPrimitive.ItemIndicator><Check size={14} /></DropdownMenuPrimitive.ItemIndicator>
              </span>
              <span style={{ flex: 1 }}>{o.label}</span>
              {o.hint ? <span className="ak-muted" style={{ fontSize: 12 }}>{o.hint}</span> : null}
            </DropdownMenuPrimitive.CheckboxItem>
          ))}
          {shown.length === 0 ? <div className="ak-muted" style={{ padding: '6px 8px', fontSize: 13 }}>No matches</div> : null}
        </div>
        {active ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onChange([])}>Clear filter</DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Row / panel overflow menu.
 *   items [{ key, label, onSelect, danger?, disabled?, separatorBefore? }]
 */
export function MoreMenu({ items, label = 'More actions', trigger, align = 'end' }) {
  const visible = items.filter(Boolean);
  if (!visible.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger || (
          <button type="button" className="ak-btn ak-ghost ak-icon ak-small" aria-label={label} onClick={(e) => e.stopPropagation()}>
            <MoreHorizontal aria-hidden="true" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-[200px]" onClick={(e) => e.stopPropagation()}>
        {visible.map((it) => (
          <React.Fragment key={it.key}>
            {it.separatorBefore ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem
              disabled={it.disabled}
              onSelect={it.onSelect}
              className={it.danger ? 'text-[var(--color-error-ink)] focus:text-[var(--color-error-ink)]' : undefined}
            >
              {it.label}
            </DropdownMenuItem>
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function useEscape(open, onEscape) {
  const ref = useRef(onEscape);
  ref.current = onEscape;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // A Radix menu or popover inside handles its own Escape first.
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return;
      ref.current && ref.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
}

function useBodyLock(open) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Hides the floating assistant dock, which would sit on the panel footer.
    document.body.classList.add('ak-panel-open');
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove('ak-panel-open');
    };
  }, [open]);
}

/**
 * Right-hand side panel. `onRequestClose` is called for Esc, scrim click and
 * the close button — the page decides whether unsaved changes block it.
 */
export function SidePanel({ open, onRequestClose, label, narrow, children }) {
  useEscape(open, onRequestClose);
  useBodyLock(open);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="ak-layer">
      <div className="ak-scrim" onClick={onRequestClose} aria-hidden="true" />
      <aside className={`ak-panel${narrow ? ' ak-narrow' : ''}`} role="dialog" aria-modal="true" aria-label={label}>
        {children}
      </aside>
    </div>,
    document.body
  );
}

export function PanelHeader({ avatar, title, badges, sub, onClose, actions, tabs, activeTab, onTabChange, bordered }) {
  return (
    <div className={`ak-panel-head${bordered ? ' ak-bordered' : ''}`}>
      <div className="ak-panel-title">
        {avatar}
        <div className="ak-panel-text">
          <h2>{title}{badges}</h2>
          {sub ? <div className="ak-panel-sub">{sub}</div> : null}
        </div>
        <button type="button" className="ak-btn ak-ghost ak-icon" aria-label="Close" onClick={onClose}>
          <X aria-hidden="true" />
        </button>
      </div>
      {actions ? <div className="ak-panel-actions">{actions}</div> : null}
      {Array.isArray(tabs) && tabs.length ? (
        <div className="ak-panel-tabs" role="tablist">
          {tabs.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={activeTab === t.key} onClick={() => onTabChange(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function PanelBody({ children, sunken }) {
  return <div className={`ak-panel-body${sunken ? ' ak-sunken' : ''}`}>{children}</div>;
}

/**
 * Footer with the unsaved-changes count and Discard / Save.
 * `left` replaces the dirty indicator (e.g. a Delete button).
 */
export function SaveBar({ dirtyCount = 0, saving, onDiscard, onSave, saveLabel = 'Save changes', left, disabled }) {
  return (
    <footer className="ak-panel-foot">
      {left || (dirtyCount > 0 ? (
        <span className="ak-dirty">{dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}</span>
      ) : <span className="ak-muted" style={{ fontSize: 13 }}>No changes</span>)}
      <span className="ak-foot-spacer" />
      {onDiscard ? <Button onClick={onDiscard} disabled={saving}>{dirtyCount > 0 ? 'Discard' : 'Close'}</Button> : null}
      {onSave ? (
        <Button variant="primary" onClick={onSave} disabled={saving || disabled || dirtyCount === 0}>
          {saving ? 'Saving…' : saveLabel}
        </Button>
      ) : null}
    </footer>
  );
}

/**
 * Confirmation dialog. `confirmText` (e.g. the org code) makes the user type
 * it before the confirm button enables — use it for irreversible actions.
 */
export function ConfirmDialog({
  open, title, children, confirmLabel = 'Confirm', danger, busy, confirmText, onConfirm, onCancel, wide,
}) {
  const [typed, setTyped] = useState('');
  const inputId = useId();
  useEscape(open, busy ? null : onCancel);
  useEffect(() => { if (!open) setTyped(''); }, [open]);
  if (!open) return null;
  const blocked = confirmText ? typed.trim() !== String(confirmText) : false;
  return ReactDOM.createPortal(
    <div className="ak-layer ak-dialog-wrap" onClick={busy ? undefined : onCancel}>
      <div className={`ak-dialog${wide ? ' ak-wide' : ''}`} role="alertdialog" aria-modal="true" aria-label={typeof title === 'string' ? title : 'Confirm'} onClick={(e) => e.stopPropagation()}>
        <div className="ak-dialog-body">
          <h2>{title}</h2>
          {children}
          {confirmText ? (
            <div className="ak-field">
              <label className="ak-label" htmlFor={inputId}>Type <span className="ak-mono">{confirmText}</span> to confirm</label>
              <input id={inputId} className="ak-input" value={typed} autoFocus onChange={(e) => setTyped(e.target.value)} />
            </div>
          ) : null}
        </div>
        <div className="ak-dialog-foot">
          <Button onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant={danger ? 'danger-solid' : 'primary'} onClick={onConfirm} disabled={busy || blocked}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Small helper: count changed top-level keys between two plain objects. */
export function countChanges(a, b, keys) {
  const ks = keys || Array.from(new Set([...Object.keys(a || {}), ...Object.keys(b || {})]));
  return ks.reduce((n, k) => n + (JSON.stringify((a || {})[k]) === JSON.stringify((b || {})[k]) ? 0 : 1), 0);
}

export function relativeTime(value) {
  if (!value) return null;
  const t = new Date(value).getTime();
  if (!Number.isFinite(t)) return null;
  const diff = Date.now() - t;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = new Date(t);
  const days = Math.round(hr / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}
